import { useEffect, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle, STATUS_COLOR } from "../roads/_roadsCommon";

const STATUS_OK = { PASS: "#22c55e", FAIL: "#ef4444",
                       WARN_LOW_SCOUR: "#f59e0b", WARN_HIGH_SCOUR: "#f59e0b" };

/** Ch 22.1 — Parametric Bridge Modeler. */
export default function BridgeModelerPanel({ projectId }) {
  const [palette, setPalette] = useState(null);
  const [bridges, setBridges] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({
    name: "Bridge-1", super_type: "precast-i-girder",
    sub_type: "pier-column", foundation_type: "pile",
    spans_csv: "30,30,30", deck_width_m: 12, loading: "Class A",
  });
  const [err, setErr] = useState("");

  const reload = async () => {
    try {
      const [p, b] = await Promise.all([
        api.get(`/projects/${projectId}/structures/palette`),
        api.get(`/projects/${projectId}/structures/bridges`),
      ]);
      setPalette(p.data); setBridges(b.data.bridges || []);
      if (b.data.bridges?.[0]) setActive(b.data.bridges[0]);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    const spans = form.spans_csv.split(",").map((s) => parseFloat(s)).filter((n) => n > 0);
    if (!spans.length) return alert("Enter at least one span");
    try {
      const { data } = await api.post(`/projects/${projectId}/structures/bridges`,
        { ...form, spans_m: spans });
      setActive(data.bridge); reload();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete bridge?")) return;
    await api.delete(`/projects/${projectId}/structures/bridges/${id}`);
    reload(); if (active?.id === id) setActive(null);
  };

  const sum = active?.design_summary;

  return (
    <div data-testid="bridge-modeler" style={{ display: "grid",
            gridTemplateColumns: "320px 1fr 280px", gap: 8, height: "calc(100vh - 280px)", minHeight: 700 }}>
      <div style={{ ...card, padding: 8 }}>
        <div style={label}>New bridge</div>
        <div style={{ marginTop: 6, fontSize: 10 }}>Name</div>
        <input data-testid="bridge-name" style={inputStyle} value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <div style={{ marginTop: 4, fontSize: 10 }}>Spans (CSV in m)</div>
        <input data-testid="bridge-spans" style={inputStyle} value={form.spans_csv}
          onChange={(e) => setForm({ ...form, spans_csv: e.target.value })} />
        <div style={{ marginTop: 4, fontSize: 10 }}>Superstructure</div>
        <select data-testid="bridge-super" style={inputStyle} value={form.super_type}
          onChange={(e) => setForm({ ...form, super_type: e.target.value })}>
          {(palette?.superstructure || []).map((s) =>
            <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <div style={{ marginTop: 4, fontSize: 10 }}>Substructure</div>
        <select data-testid="bridge-sub" style={inputStyle} value={form.sub_type}
          onChange={(e) => setForm({ ...form, sub_type: e.target.value })}>
          {(palette?.substructure || []).map((s) =>
            <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <div style={{ marginTop: 4, fontSize: 10 }}>Foundation</div>
        <select data-testid="bridge-found" style={inputStyle} value={form.foundation_type}
          onChange={(e) => setForm({ ...form, foundation_type: e.target.value })}>
          {(palette?.foundation || []).map((s) =>
            <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <div style={{ marginTop: 4, fontSize: 10 }}>Deck width (m) · Loading</div>
        <div style={{ display: "flex", gap: 4 }}>
          <input data-testid="bridge-width" style={inputStyle} type="number"
            value={form.deck_width_m} onChange={(e) =>
              setForm({ ...form, deck_width_m: parseFloat(e.target.value) || 0 })} />
          <select data-testid="bridge-loading" style={inputStyle} value={form.loading}
            onChange={(e) => setForm({ ...form, loading: e.target.value })}>
            <option>Class A</option><option>Class 70R</option><option>Class AA</option>
          </select>
        </div>
        <button data-testid="bridge-create" style={{ ...btn, width: "100%", marginTop: 12 }}
          onClick={create}>+ Create bridge</button>
        <div style={{ ...label, marginTop: 16 }}>Existing</div>
        {bridges.map((b) => (
          <button key={b.id} data-testid={`bridge-row-${b.id}`}
            style={{ ...btnGhost, width: "100%", textAlign: "left", marginTop: 3,
                      background: active?.id === b.id ? "rgba(242,107,31,.20)" : "transparent" }}
            onClick={() => setActive(b)}>
            <b>{b.name}</b> · {b.design_summary?.n_spans} × {b.spans_m?.[0]}m
            · {b.deck_width_m}m wide
          </button>
        ))}
      </div>

      <div style={{ ...card, padding: 16, overflowY: "auto" }}>
        {!active ? (
          <div style={{ color: "var(--pf-muted)", fontSize: 12 }}>Pick or create a bridge</div>
        ) : !sum ? (
          <div style={{ color: "var(--pf-muted)", fontSize: 12 }}>
            Bridge "{active.name}" has no design summary yet — re-create or
            edit it via the +New Bridge form to compute spans/loads.
          </div>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <div>
                <div style={{ ...label, marginBottom: 2 }}>{(active.super_type || "rcc-slab").replace(/-/g, " ").toUpperCase()}</div>
                <h3 style={{ margin: 0, color: "#fff" }}>{active.name}</h3>
                <div style={{ fontSize: 11, color: "#aaa" }}>
                  {active.n_spans || sum.n_spans} spans · total {sum.total_length_m}m · deck {sum.deck_width_m}m · {sum.loading}
                </div>
              </div>
              <button data-testid={`bridge-del-${active.id}`} style={btnGhost}
                onClick={() => remove(active.id)}>Delete</button>
            </div>

            {/* Schematic — elevation view */}
            <svg data-testid="bridge-schematic" viewBox={`-30 -120 ${sum.total_length_m + 60} 200`}
                  style={{ width: "100%", height: 240, background: "#0a1220", marginTop: 16 }}>
              {/* Deck */}
              {(() => {
                let x = 0; const rects = [];
                sum.spans.forEach((sp, i) => {
                  rects.push(<rect key={`d-${i}`} x={x} y={-30} width={sp.span_m} height={sp.depth_m * 8 + 10}
                                        fill="#3aa0ff" stroke="#fff" strokeWidth={0.4} />);
                  x += sp.span_m;
                });
                return rects;
              })()}
              {/* Piers */}
              {(() => {
                let x = 0; const piers = [];
                sum.spans.forEach((sp, i) => {
                  if (i < sum.spans.length - 1) {
                    x += sp.span_m;
                    piers.push(<rect key={`p-${i}`} x={x - 1.5} y={-20} width={3} height={50}
                                          fill="#888" stroke="#fff" strokeWidth={0.4} />);
                  }
                });
                return piers;
              })()}
              {/* Abutments */}
              <rect x={-3} y={-20} width={3} height={50} fill="#666" />
              <rect x={sum.total_length_m} y={-20} width={3} height={50} fill="#666" />
              {/* Ground */}
              <line x1={-30} y1={32} x2={sum.total_length_m + 30} y2={32}
                      stroke="#7a4f1b" strokeWidth={2} strokeDasharray="3,2" />
              {/* Span labels */}
              {(() => {
                let x = 0; const t = [];
                sum.spans.forEach((sp, i) => {
                  t.push(<text key={`t-${i}`} x={x + sp.span_m / 2} y={-50}
                                     fontSize={6} fill="#fff" textAnchor="middle">
                    Span {i + 1} · {sp.span_m}m · M={Math.round(sp.bending_moment_kn_m)}kNm/m
                  </text>);
                  x += sp.span_m;
                });
                return t;
              })()}
            </svg>

            {/* Span analysis table */}
            <table data-testid="bridge-spans-table"
              style={{ width: "100%", marginTop: 16, fontSize: 11, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.05)", textAlign: "left" }}>
                  <th style={{ padding: 4 }}>Span</th><th style={{ padding: 4 }}>L (m)</th>
                  <th style={{ padding: 4 }}>d (m)</th><th style={{ padding: 4 }}>DL (kN/m)</th>
                  <th style={{ padding: 4 }}>LL (kN/m)</th><th style={{ padding: 4 }}>M (kNm)</th>
                  <th style={{ padding: 4 }}>V (kN)</th>
                </tr>
              </thead>
              <tbody>
                {sum.spans.map((sp, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                    <td style={{ padding: 4 }}>{i + 1}</td>
                    <td style={{ padding: 4 }}>{sp.span_m}</td>
                    <td style={{ padding: 4 }}>{sp.depth_m}</td>
                    <td style={{ padding: 4 }}>{sp.dead_load_kn_per_m}</td>
                    <td style={{ padding: 4 }}>{sp.live_load_kn_per_m}</td>
                    <td style={{ padding: 4 }}>{Math.round(sp.bending_moment_kn_m)}</td>
                    <td style={{ padding: 4 }}>{Math.round(sp.shear_kn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      <div style={{ ...card, padding: 8 }}>
        <div style={label}>Help</div>
        <p style={{ fontSize: 10, color: "#aaa", lineHeight: 1.6 }}>
          Configure span arrangement + types. The system picks default girder depth (span/15…/22),
          computes self-weight + wearing-course dead load, applies IRC 6 Class A/70R/AA live load UDL,
          and reports simple-supported moment & shear per span. Substructure + foundation choices
          are saved on the bridge document for the BIM coordination model.
        </p>
        {err && <div style={{ marginTop: 8, color: "#FF8888", fontSize: 11 }}>{err}</div>}
      </div>
    </div>
  );
}
